import { isAxiosError } from 'axios';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { CustomPackageImportDrawer } from './custom-package-import/custom-package-import-drawer';
import { ImportStatus } from '../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../components/csv-import/data/types/import-type';
import { convertDimensions } from '../../../../services/unit-services/dimensions/dimension-convert-service';
import Global from '../../../../store/global-selectors';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import { useModal } from '../../../../hooks/use-modal';
import { PackingRecommendations } from '../../../../constants/features';
import { refreshPackagesRequest, refreshPackagesSuccess } from '../../../../store/seller/seller.actions';
import { exportCustomPackages } from '../../../../store/export/custom-packages/custom-packages-export.actions';
import { CUSTOM_PACKAGES_EXPORT_CLICKED } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { notifications } from '../../components/notifications';
import { addCustomPackage, deleteCustomPackage, editCustomPackage } from '../api';
import { CUSTOM_PACKAGE_DEFAULTS, MODALS } from '../constants';
import { CustomPackagesModal } from './custom-packages-modal';
import { CustomPackagesTable } from './custom-packages-table';
import DeleteModal from './delete-modal';
import { messages, tableMessages } from './messages';
import { PackageService } from '../../../../services/package-service';
import { EventSourceStrings, Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import packagesSettingsStyle from '../packages-settings.module.css';
import { useEnableSupplyPage } from '../../../../feature-flags/hooks/use-enable-supply-page';
import { SuppliesCardContainer } from '../../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../../home-page/version2/supplies-cards/supplies-page-constants';

type FormValues = {
  name?: string;
  dimensions?: unknown;
  tareWeight: number | null;
  maxWeight: number | null;
  weightBuffer: number | null;
  [key: string]: unknown;
};

export const CustomPackages = () => {
  const dispatch = useDispatch();
  const enableSupplyPage = useEnableSupplyPage();

  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);
  const sellerDimUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const rawWeightUnitEnum = useSelector(Global.selectSellerRawWeightUnitEnum);
  const packages = useSelector(Global.selectSellerPackages);
  const [modalAction, setModalAction] = useAsyncSafeState(undefined);
  const [packageInfo, setPackageInfo] = useAsyncSafeState(undefined);
  const trackCustomPackageFlow = useTrackWorkflow(Workflows.CUSTOM_PACKAGE_FLOW);
  const trackCsvImportFlow = useTrackWorkflow(Workflows.CSV_CUSTOM_PACKAGE_IMPORT_FLOW);
  const { open: openImportDrawer, isOpen: isImportDrawerOpen, close: closeImportDrawer } = useModal();
  const latestCsvImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.customPackage));

  const filteredPackages = useMemo(() => packages.filter((p) => p.isCustom), [packages]);
  const tableData = useMemo(
    () =>
      filteredPackages.map((p) => {
        const convertDim = convertDimensions(p.dimensions, sellerDimUnit);
        return {
          packageTypeId: p.packageTypeId,
          name: p.name,
          length: convertDim.length,
          width: convertDim.width,
          height: convertDim.height,
          dimensions: convertDim,
          tareWeight: p.tareWeight,
          maxWeight: p.maxWeight,
          weightBuffer: p.weightBuffer,
          weightUnit: p.weightUnit,
          isCustom: p.isCustom,
        };
      }),
    [filteredPackages, sellerDimUnit],
  );

  const handleModalClose = useCallback(() => setModalAction(undefined), [setModalAction]);

  const handleDelete = useCallback(() => {
    const { packageTypeId } = packageInfo;
    const fetchData = async () => {
      try {
        await deleteCustomPackage(packageTypeId);
        const updatedPackages = packages.filter((p) => p.packageTypeId !== packageTypeId);
        dispatch(refreshPackagesSuccess(updatedPackages));
        handleModalClose();
        notifications.success();
        trackCustomPackageFlow(WorkflowSteps.COMPLETE, { packageTypeId });
      } catch (e) {
        notifications.error(e);
        trackCustomPackageFlow(WorkflowSteps.ERROR, { error: e });
      }
    };

    fetchData();
  }, [dispatch, handleModalClose, packageInfo, packages, trackCustomPackageFlow]);

  const handleEditPackage = useCallback(
    (packageTypeId: string) => {
      const selectedPackage = tableData.find((p) => p.packageTypeId === packageTypeId);
      setModalAction(MODALS.Edit);
      setPackageInfo(selectedPackage);
      trackCustomPackageFlow(WorkflowSteps.START, {
        action: MODALS.Edit,
        source: EventSourceStrings.SETTINGS_PACKAGES_PAGE,
      });
    },
    [setModalAction, setPackageInfo, tableData, trackCustomPackageFlow],
  );

  const handleDeletePackage = useCallback(
    (packageTypeId: string) => {
      const selectedPackage = tableData.find((p) => p.packageTypeId === packageTypeId);
      setModalAction(MODALS.Delete);
      setPackageInfo(selectedPackage);
      trackCustomPackageFlow(WorkflowSteps.START, {
        action: MODALS.Delete,
        source: EventSourceStrings.SETTINGS_PACKAGES_PAGE,
      });
    },
    [setModalAction, setPackageInfo, tableData, trackCustomPackageFlow],
  );

  const handleAddPackage = useCallback(() => {
    setModalAction(MODALS.Add);
    setPackageInfo(undefined);
    trackCustomPackageFlow(WorkflowSteps.START, {
      action: MODALS.Add,
      source: EventSourceStrings.SETTINGS_PACKAGES_PAGE,
    });
  }, [setModalAction, setPackageInfo, trackCustomPackageFlow]);

  const handleImportPackages = useCallback(() => {
    if (latestCsvImport?.status !== ImportStatus.ACTIVE) {
      trackCsvImportFlow(WorkflowSteps.START, { source: EventSourceStrings.SETTINGS_PACKAGES_PAGE });
    }
    openImportDrawer();
  }, [latestCsvImport?.status, openImportDrawer, trackCsvImportFlow]);

  const handleImportSuccess = useCallback(() => {
    dispatch(refreshPackagesRequest());
  }, [dispatch]);

  const handleExportPackages = useCallback(() => {
    Segment.trackEvent(CUSTOM_PACKAGES_EXPORT_CLICKED, {
      includeTareWeight: isPackingRecommendationsEnabled,
      includeMaxWeight: isPackingRecommendationsEnabled,
      includeWeightBuffer: isPackingRecommendationsEnabled,
    });
    dispatch(
      exportCustomPackages({
        includeTareWeight: isPackingRecommendationsEnabled,
        includeMaxWeight: isPackingRecommendationsEnabled,
        includeWeightBuffer: isPackingRecommendationsEnabled,
      }),
    );
  }, [dispatch, isPackingRecommendationsEnabled]);

  const updatedPackages = useCallback(
    (updatedPackage: Record<string, unknown>) => {
      const packagesActions = {
        add: () => [...packages, updatedPackage],
        edit: () =>
          packages.map((p) => ({
            ...p,
            ...(p.packageTypeId === updatedPackage.packageTypeId && {
              ...updatedPackage,
            }),
          })),
      };
      return packagesActions[modalAction as MODALS.Add | MODALS.Edit]();
    },
    [modalAction, packages],
  );

  const handleSave = useCallback(
    (formValues: FormValues, setModalError: (value: string | null) => void, setIsSaving: (value: boolean) => void) => {
      const customPackage = Object.entries(CUSTOM_PACKAGE_DEFAULTS).reduce(
        (result, [key, defaultValue]) => ({
          ...result,
          [key]: formValues[key] || defaultValue,
        }),
        {},
      );
      const payload = {
        ...customPackage,
        ...(isPackingRecommendationsEnabled && {
          tareWeight: formValues.tareWeight !== null && formValues.tareWeight !== undefined ? { value: formValues.tareWeight } : null,
        }),
        ...(isPackingRecommendationsEnabled && {
          maxWeight: formValues.maxWeight !== null && formValues.maxWeight !== undefined ? { value: formValues.maxWeight } : null,
          weightBuffer:
            formValues.weightBuffer !== null && formValues.weightBuffer !== undefined ? { value: formValues.weightBuffer } : null,
          weightUnit: rawWeightUnitEnum,
        }),
      };

      const API = {
        add: () => addCustomPackage(payload),
        edit: () => editCustomPackage(payload),
      };

      const fetchData = async () => {
        setIsSaving(true);
        setModalError(null);
        try {
          const updatedPackage = (await API[modalAction as MODALS.Add | MODALS.Edit]()) || payload;
          updatedPackage.packageId = PackageService.buildCompositePackageId(updatedPackage);
          dispatch(refreshPackagesSuccess(updatedPackages(updatedPackage)));
          notifications.success();
          trackCustomPackageFlow(WorkflowSteps.COMPLETE, { updatedPackage });
          handleModalClose();
        } catch (e: unknown) {
          let errorMessage = messages['error-saving-package'].defaultMessage;
          if (isAxiosError(e)) {
            errorMessage = e.response?.data?.Errors?.[0]?.Message || e.message;
          } else if (e instanceof Error) {
            errorMessage = e.message || errorMessage;
          }

          setModalError(errorMessage);
          trackCustomPackageFlow(WorkflowSteps.ERROR, { error: e });
        } finally {
          setIsSaving(false);
        }
      };

      fetchData();
    },
    [dispatch, handleModalClose, isPackingRecommendationsEnabled, modalAction, rawWeightUnitEnum, trackCustomPackageFlow, updatedPackages],
  );

  return (
    <>
      <div className={packagesSettingsStyle['tab-description']}>
        <Text textType={TextType.Paragraph} value={messages.description} />
      </div>
      <FlexRow className={packagesSettingsStyle['table-header']}>
        <Text textType={TextType.H3} value={tableMessages.header.title} />
        <FlexRow gap={Gap.Space12}>
          <Button onClick={handleImportPackages} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...tableMessages.header.import} />
          </Button>
          <Button disabled={!tableData.length} onClick={handleExportPackages} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...tableMessages.header.export} />
          </Button>
          <Button onClick={handleAddPackage} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...tableMessages.header.add} />
          </Button>
        </FlexRow>
      </FlexRow>
      <CustomPackagesTable
        data={tableData}
        onDeletePackage={handleDeletePackage}
        onEditPackage={handleEditPackage}
        sellerDefaultDimensionsUnit={sellerDimUnit}
        sellerDefaultWeightUnit={sellerWeightUnit}
      />
      {(modalAction === MODALS.Add || modalAction === MODALS.Edit) && (
        <CustomPackagesModal
          action={modalAction}
          onClose={handleModalClose}
          onSave={handleSave}
          packageInfo={packageInfo}
          unit={sellerDimUnit}
        />
      )}
      {modalAction === MODALS.Delete && <DeleteModal onClose={handleModalClose} onDelete={handleDelete} packageName={packageInfo?.name} />}
      {isImportDrawerOpen && (
        <CustomPackageImportDrawer onClose={closeImportDrawer} onSuccess={handleImportSuccess} trackProcess={trackCsvImportFlow} />
      )}
      {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES} />}
    </>
  );
};
